import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { useInterviewQuestionAIVideoContext } from "hooks";
import { AI_VIDEO_TASK_DISPLAY_STATE, getPreferredGeneratedVideoUrl, getVideoTaskDisplayState } from "constants/index";
import { InterviewQuestionAIVideoPreviewModal, InterviewQuestionAIVideoStatusModal } from "components/Missions/Roles";

/**
 * InterviewQuestionAIVideoModal
 *
 * This modal handles the display and interaction for AI-generated interview question videos.
 * It shows different states based on the video generation task status, including pending, success, and error states.
 */
const InterviewQuestionAIVideoModal = (props) => {
  const intl = useIntl();
  const {
    aiVideoQuestion: question,
    showAiVideoModal: show,
    closeAiVideoModal: onClose,
    reopenVideoActionModal,
    isSubmittingNewAttempt,
    confirmTask,
    cancelTask,
    getTask,
  } = useInterviewQuestionAIVideoContext();

  const [submitting, setSubmitting] = useState(false);
  const task = getTask(question?.id);
  const isSubmittingFreshAttempt = isSubmittingNewAttempt(question?.id);

  const videoSrc = useMemo(() => getPreferredGeneratedVideoUrl(task), [task]);
  const displayState = getVideoTaskDisplayState(task, {
    isSubmittingFreshAttempt,
    emptyState: AI_VIDEO_TASK_DISPLAY_STATE.PROCESSING,
  });

  const runTaskAction = async (action, { shouldCloseOnSuccess = false } = {}) => {
    setSubmitting(true);
    const result = await action(question.id);
    setSubmitting(false);

    if (result && shouldCloseOnSuccess) onClose();

    return result;
  };

  const handleConfirm = async () => runTaskAction(confirmTask, { shouldCloseOnSuccess: true });

  const handleCancelJob = async () => runTaskAction(cancelTask, { shouldCloseOnSuccess: true });

  const statusContent = getStatusDisplay(task?.status, intl, { isSubmittingFreshAttempt });
  const pendingSupportingContentValues = {
    userName: <strong>{task?.submittedByName}</strong>,
  };
  const confirmationSupportingContentValues = {
    userName: <strong>{task?.confirmedByName}</strong>,
  };

  if (displayState === AI_VIDEO_TASK_DISPLAY_STATE.FINALIZING) {
    return (
      <InterviewQuestionAIVideoConfirmation
        task={task}
        show={show}
        onClose={onClose}
        handleCancelJob={handleCancelJob}
        submitting={submitting}
        statusContent={statusContent}
        supportingContentValues={confirmationSupportingContentValues}
        {...props}
      />
    );
  }

  if ([AI_VIDEO_TASK_DISPLAY_STATE.SUBMITTING, AI_VIDEO_TASK_DISPLAY_STATE.PROCESSING].includes(displayState)) {
    return (
      <InterviewQuestionAIVideoStatusModal
        task={task}
        show={show}
        onClose={onClose}
        onCancel={handleCancelJob}
        loading={submitting || isSubmittingFreshAttempt}
        titleContentMsgId="Missions.Roles.RoleVetting.Interviews.AIVideo.Processing.Title"
        descriptionContentMsgId="Missions.Roles.RoleVetting.Interviews.AIVideo.Processing.Description"
        supportingContentMsgId={
          task?.submittedByName && "Missions.Roles.RoleVetting.Interviews.AIVideo.Processing.SubmittedBy"
        }
        supportingContentValues={pendingSupportingContentValues}
        showElapsedInHeader={true}
        statusContent={statusContent}
        {...props}
      />
    );
  }

  if (displayState === AI_VIDEO_TASK_DISPLAY_STATE.REVIEW) {
    return (
      <InterviewQuestionAIVideoPreviewModal
        show={show}
        question={question}
        task={task}
        videoSrc={videoSrc}
        onClose={onClose}
        onConfirm={handleConfirm}
        onDiscard={handleCancelJob}
        loading={submitting}
        {...props}
      />
    );
  }

  return (
    <InterviewQuestionAIVideoError
      task={task}
      show={show}
      onClose={onClose}
      handleCancelJob={() => reopenVideoActionModal(question, { ignoreExistingTaskOnGenerate: true })}
      {...props}
    />
  );
};

const InterviewQuestionAIVideoError = ({ task, show, onClose, handleCancelJob, ...props }) => (
  <InterviewQuestionAIVideoStatusModal
    show={show}
    onClose={onClose}
    onCancel={handleCancelJob}
    loading={false}
    showLoader={false}
    secondaryButtonMessageId="Missions.Roles.RoleVetting.Interviews.AIVideo.Hide"
    desctructableButtonMessageId="Missions.Roles.RoleVetting.Interviews.AIVideo.GenerateAgain"
    titleContentMsgId="Missions.Roles.RoleVetting.Interviews.AIVideo.Error.Title"
    descriptionContentMsgId="Missions.Roles.RoleVetting.Interviews.AIVideo.Error.Description"
    statusContent={task?.errorMessage || task?.status}
    {...props}
  />
);

const InterviewQuestionAIVideoConfirmation = ({
  task,
  show,
  onClose,
  handleCancelJob,
  submitting,
  statusContent,
  supportingContentValues,
  ...props
}) => (
  <InterviewQuestionAIVideoStatusModal
    task={task}
    show={show}
    onClose={onClose}
    onCancel={handleCancelJob}
    loading={submitting}
    titleContentMsgId="Missions.Roles.RoleVetting.Interviews.AIVideo.Uploading.Title"
    descriptionContentMsgId="Missions.Roles.RoleVetting.Interviews.AIVideo.Uploading.Description"
    supportingContentMsgId={
      task?.confirmedByName && "Missions.Roles.RoleVetting.Interviews.AIVideo.Uploading.ConfirmedBy"
    }
    supportingContentValues={supportingContentValues}
    statusContent={statusContent}
    {...props}
  />
);

const getStatusDisplay = (status, intl, { isSubmittingFreshAttempt = false } = {}) => {
  if (isSubmittingFreshAttempt || !status) {
    return <FormattedMessage id="Missions.Roles.RoleVetting.Interviews.AIVideo.Processing.Status.Pending" />;
  }

  const translationKey = `AIRoleBuilder.JobStatus.${status}`;

  if (intl.messages[translationKey]) {
    return <FormattedMessage id={translationKey} />;
  }

  return status;
};

InterviewQuestionAIVideoModal.propTypes = {
  show: PropTypes.bool,
};

InterviewQuestionAIVideoError.propTypes = {
  task: PropTypes.object,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleCancelJob: PropTypes.func.isRequired,
};

InterviewQuestionAIVideoConfirmation.propTypes = {
  task: PropTypes.object,
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleCancelJob: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  statusContent: PropTypes.node.isRequired,
  supportingContentValues: PropTypes.object,
};

export default InterviewQuestionAIVideoModal;
